import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Download, TrendingUp, Zap, Target, Package, ChevronRight } from 'lucide-react';
import API_BASE_URL from '../config/api';

const PerformanceIncome = () => {
    const [loading, setLoading] = useState(true);
    const [incomeData, setIncomeData] = useState([]);
    const [binaryPerformance, setBinaryPerformance] = useState({
        eligible: false,
        left_packages: 0,
        right_packages: 0,
        required: 5,
        left_breakdown: [],
        right_breakdown: []
    });

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/user/income_performance.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setIncomeData(Array.isArray(result.data) ? result.data : []);
                if (result.binary_performance) {
                    setBinaryPerformance({
                        ...result.binary_performance,
                        left_breakdown: Array.isArray(result.binary_performance.left_breakdown) ? result.binary_performance.left_breakdown : [],
                        right_breakdown: Array.isArray(result.binary_performance.right_breakdown) ? result.binary_performance.right_breakdown : []
                    });
                }
            }
        } catch (error) {
            console.error("Failed to fetch performance income", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const formatCurrency = (amount) => {
        return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            </Layout>
        );
    }

    // Safely calculate total earnings
    const totalEarnings = Array.isArray(incomeData)
        ? incomeData.reduce((acc, curr) => acc + parseFloat(curr?.bonus || 0), 0)
        : 0;

    return (
        <Layout>
            <div className="p-4 sm:p-6 space-y-8 max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-black text-gray-900">Performance Station</h1>
                        <p className="text-gray-500 text-sm mt-1 font-medium">12-Hour Cycle Targets & Achievements</p>
                    </div>
                </div>

                {/* Performance Goal Tracking */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left & Right Tracking */}
                    <div className="bg-white rounded-[2.5rem] p-8 shadow-xl border-2 border-indigo-50 relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-50/50 rounded-bl-full -mr-24 -mt-24 transition-all group-hover:scale-110"></div>

                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-3 bg-indigo-600 rounded-2xl text-white shadow-lg shadow-indigo-200">
                                <Zap size={28} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-black text-gray-900 leading-tight">Cycle Sprint</h2>
                                <p className="text-indigo-600 font-black text-[10px] uppercase tracking-widest">Active 12-Hour Cycle</p>
                            </div>
                        </div>

                        <div className="space-y-8 relative z-10">
                            {/* Left Side Progress */}
                            <div className="space-y-3">
                                <div className="flex justify-between items-end">
                                    <div>
                                        <p className="text-[10px] font-black text-gray-400 uppercase mb-1">Left Subtree Packages</p>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-3xl font-black text-gray-900">{binaryPerformance?.left_packages || 0}</span>
                                            <span className="text-sm font-bold text-gray-400">/ {binaryPerformance?.required || 5}</span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className={`text-[10px] font-black px-3 py-1 rounded-full ${(binaryPerformance?.left_packages >= (binaryPerformance?.required || 5)) ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                            {(binaryPerformance?.left_packages >= (binaryPerformance?.required || 5)) ? 'TARGET REACHED' : 'IN PROGRESS'}
                                        </span>
                                    </div>
                                </div>
                                <div className="h-4 w-full bg-indigo-50 rounded-full overflow-hidden p-1 shadow-inner">
                                    <div
                                        className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full transition-all duration-1000 shadow-sm"
                                        style={{ width: `${Math.min(100, ((binaryPerformance?.left_packages || 0) / (binaryPerformance?.required || 5)) * 100)}%` }}
                                    ></div>
                                </div>
                                {/* Pkg Breakdown */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2">
                                    {binaryPerformance?.left_breakdown?.map((item, i) => (
                                        <div key={i} className={`bg-gray-50 rounded-xl p-2 border ${item?.matched > 0 ? 'border-green-200 bg-green-50' : 'border-gray-100'} text-center`}>
                                            <p className="text-[8px] font-black text-gray-400 uppercase">{item?.pv || 0} PV</p>
                                            <p className={`text-sm font-black ${item?.matched > 0 ? 'text-green-600' : 'text-indigo-600'}`}>{item?.count || 0} Qty</p>
                                            <p className="text-[9px] font-bold text-gray-400 mt-1">Matched: {item?.matched || 0}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Right Side Progress */}
                            <div className="space-y-3">
                                <div className="flex justify-between items-end">
                                    <div>
                                        <p className="text-[10px] font-black text-gray-400 uppercase mb-1">Right Subtree Packages</p>
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-3xl font-black text-gray-900">{binaryPerformance?.right_packages || 0}</span>
                                            <span className="text-sm font-bold text-gray-400">/ {binaryPerformance?.required || 5}</span>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className={`text-[10px] font-black px-3 py-1 rounded-full ${(binaryPerformance?.right_packages >= (binaryPerformance?.required || 5)) ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                            {(binaryPerformance?.right_packages >= (binaryPerformance?.required || 5)) ? 'TARGET REACHED' : 'IN PROGRESS'}
                                        </span>
                                    </div>
                                </div>
                                <div className="h-4 w-full bg-indigo-50 rounded-full overflow-hidden p-1 shadow-inner">
                                    <div
                                        className="h-full bg-gradient-to-r from-amber-500 to-orange-600 rounded-full transition-all duration-1000 shadow-sm"
                                        style={{ width: `${Math.min(100, ((binaryPerformance?.right_packages || 0) / (binaryPerformance?.required || 5)) * 100)}%` }}
                                    ></div>
                                </div>
                                {/* Pkg Breakdown */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2">
                                    {binaryPerformance?.right_breakdown?.map((item, i) => (
                                        <div key={i} className={`bg-gray-50 rounded-xl p-2 border ${item?.matched > 0 ? 'border-green-200 bg-green-50' : 'border-gray-100'} text-center`}>
                                            <p className="text-[8px] font-black text-gray-400 uppercase">{item?.pv || 0} PV</p>
                                            <p className={`text-sm font-black ${item?.matched > 0 ? 'text-green-600' : 'text-amber-600'}`}>{item?.count || 0} Qty</p>
                                            <p className="text-[9px] font-bold text-gray-400 mt-1">Matched: {item?.matched || 0}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="mt-10 p-5 bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl text-white shadow-2xl">
                            <div className="flex justify-between items-center">
                                <div>
                                    <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">Estimated Cycle Reward</p>
                                    <h3 className={`text-2xl font-black ${binaryPerformance?.eligible ? 'text-green-400' : 'text-gray-500'}`}>
                                        {binaryPerformance?.eligible ? '₹ 2,500.00' : '₹ 0.00'}
                                    </h3>
                                </div>
                                <div className={`p-4 rounded-full ${binaryPerformance?.eligible ? 'bg-green-500' : 'bg-white/10 opacity-30 animate-pulse'}`}>
                                    <Target size={24} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Stats & Rules */}
                    <div className="flex flex-col gap-6">
                        <div className="bg-gradient-to-br from-amber-400 to-orange-500 rounded-[2.5rem] p-8 text-white shadow-xl relative overflow-hidden group flex-1">
                            <TrendingUp className="absolute -right-8 -bottom-8 w-48 h-48 text-white/10 transform -rotate-12" />
                            <div className="relative z-10">
                                <p className="text-amber-100 text-xs font-black uppercase tracking-widest mb-2">Lifetime Performance Earnings</p>
                                <h2 className="text-5xl font-black leading-tight">
                                    {formatCurrency(totalEarnings)}
                                </h2>
                                <div className="mt-4 flex items-center gap-2 text-[10px] font-bold bg-white/20 w-fit px-4 py-1.5 rounded-full border border-white/10">
                                    <span>QUALIFIED CYCLES: {incomeData?.length || 0}</span>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-[2.5rem] p-8 shadow-xl border-2 border-indigo-50 flex-1">
                            <h3 className="text-lg font-black text-gray-900 mb-4 flex items-center gap-2">
                                <Package className="text-indigo-600" size={20} />
                                Bonus Rules
                            </h3>
                            <div className="space-y-4">
                                <div className="flex items-start gap-4">
                                    <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600 mt-1"><ChevronRight size={14} /></div>
                                    <p className="text-xs text-gray-600 font-medium leading-relaxed">
                                        Must achieve <span className="text-indigo-600 font-black">5 package activations</span> (min 25 PV) on each side within a single 12-hour cycle.
                                    </p>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600 mt-1"><ChevronRight size={14} /></div>
                                    <p className="text-xs text-gray-600 font-medium leading-relaxed">
                                        Bonus is credited instantly at the end of the cycle upon system batch processing.
                                    </p>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600 mt-1"><ChevronRight size={14} /></div>
                                    <p className="text-xs text-gray-600 font-medium leading-relaxed">
                                        Unlimited cycles available daily (Morning & Evening sessions).
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* History Table */}
                <div className="bg-white rounded-[2.5rem] shadow-xl border-2 border-slate-50 overflow-hidden">
                    <div className="p-8 border-b border-gray-100 flex justify-between items-center">
                        <h3 className="text-xl font-black text-gray-900">Achievement Log</h3>
                        <button className="flex items-center gap-2 text-[10px] font-black uppercase text-indigo-600 hover:underline">
                            <Download size={16} /> Export Detailed Report
                        </button>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50/50 text-[10px] uppercase font-black tracking-widest text-gray-400">
                                <tr>
                                    <th className="px-8 py-5">Activation Cycle / Date</th>
                                    <th className="px-8 py-5 text-right">Bonus Reward</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {incomeData && incomeData.length > 0 ? incomeData.map((item, idx) => (
                                    <tr key={item?.id || idx} className="group hover:bg-indigo-50/30 transition-colors">
                                        <td className="px-8 py-6">
                                            <p className="text-sm font-black text-gray-900">{item?.month || '---'}</p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <p className="text-[10px] text-gray-400 font-bold uppercase">Performance Cycle</p>
                                                {item?.type === 'binary' && (
                                                    <span className="text-[8px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded font-black uppercase">LIVE BINARY</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <p className="text-lg font-black text-emerald-600">+ {formatCurrency(item?.bonus)}</p>
                                            <span className="text-[9px] font-bold text-gray-400 uppercase">{item?.status || 'Paid'}</span>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="2" className="px-8 py-16 text-center">
                                            <Package size={48} className="mx-auto text-gray-200 mb-4" />
                                            <p className="text-gray-400 font-black uppercase tracking-widest text-sm">No Achievement History Found</p>
                                            <p className="text-gray-300 text-xs mt-1">Start matching packages to earn performance bonuses!</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default PerformanceIncome;



